import { ref, computed, type ComputedRef } from 'vue'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import type { SimpleHealthUnitResponse } from '@/Network/Types/Responses/HealthUnitResponses'
import type {
  PinChangeOrigin,
  PinChangeReason,
  PinSuggestionItem
} from '@/Network/Types/Responses/PinSuggestionResponses'
import type { PinConfidence, PlaceCandidate } from '@/Network/Services/PlacesService'
import { haversineMeters, bearingLabel, type LatLng } from '@/utils/geo'
import { parseGoogleMapsUrl, isShortGoogleMapsUrl } from '../helpers/googleMapsUrl'
import { REACH_RADIUS_DEFAULT, isReachRadiusInRange } from '../schemas'
import { HealthUnitType, radiusPresetFor } from '../unitType'
import { PIN_MOVE_THRESHOLD_M, type PinConfirmPayload } from '../modals/UnitPinModal/pinModalTypes'

/** Fallbacks para falha sem mensagem própria (erro cru): nunca vazar texto técnico. */
const GEOCODE_FALLBACK =
  'Não foi possível localizar o endereço no mapa. Cole o link do Google Maps ou ajuste o pino no mapa.'
const MAPS_LINK_FALLBACK =
  'Não foi possível usar o link do Google Maps. Confira o link ou ajuste o pino no mapa.'

/** Coordenada em string (como vem do backend) para `LatLng`, ou `null` se não for número. */
function toLatLng(lat: string | null, lng: string | null): LatLng | null {
  const parsedLat = Number(lat)
  const parsedLng = Number(lng)
  if (lat === null || lng === null) return null
  if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) return null
  return { lat: parsedLat, lng: parsedLng }
}

/**
 * Estado e fluxos de geolocalização do formulário de unidade: geocode do
 * endereço, busca no Places, link do Google Maps (completo e curto), marcador
 * arrastável, raio de alcance e o gate de confirmação do pino (origem, motivo,
 * justificativa do raio e confirmação pela vista de satélite).
 * Falha do Google (Geocoding, link curto) não trava o pino: o toast traz a
 * mensagem do service, que aponta os outros caminhos (link, busca por nome,
 * arrastar o marcador).
 */
// eslint-disable-next-line max-lines-per-function
export function useUnitGeolocation() {
  const { notifyError, notifyErrorMessage } = useErrorToast()

  const latitude = ref<string | null>(null)
  const longitude = ref<string | null>(null)
  const originalLatitude = ref<string | null>(null)
  const originalLongitude = ref<string | null>(null)
  const formattedAddress = ref('')
  const foundLocation = ref(false)
  const locationConfirmed = ref(false)
  const googleMapsLink = ref('')
  const isLoadingLink = ref(false)
  const reachRadius = ref<number | undefined>(REACH_RADIUS_DEFAULT)

  const unitType = ref<HealthUnitType | null>(null)
  const confidence = ref<PinConfidence | null>(null)
  const origin = ref<PinChangeOrigin | null>(null)
  const placeName = ref('')
  const reason = ref<PinChangeReason | null>(null)
  const reasonDetail = ref('')
  const radiusJustification = ref('')
  const confirmedOnBuilding = ref(false)
  const suggestionId = ref<number | null>(null)
  const suggestedPoint = ref<LatLng | null>(null)
  /**
   * Raio já salvo na unidade. `undefined` no cadastro: lá qualquer raio é uma
   * escolha desta sessão, então vale a regra do preset desde o primeiro valor.
   */
  const originalRadius = ref<number | undefined>(undefined)

  /**
   * O raio precisa estar na faixa aceita pelo backend — confirmar a localização
   * com um valor fora dela só adiava a falha para o 400 do submit.
   */
  const canConfirm = computed(() => isReachRadiusInRange(reachRadius.value))

  const canResetLocation = computed(() => !!originalLatitude.value && !!originalLongitude.value)

  const currentPoint: ComputedRef<LatLng | null> = computed(() =>
    toLatLng(latitude.value, longitude.value)
  )
  const originalPoint: ComputedRef<LatLng | null> = computed(() =>
    toLatLng(originalLatitude.value, originalLongitude.value)
  )

  const movedDistanceM = computed(() =>
    currentPoint.value && originalPoint.value
      ? haversineMeters(originalPoint.value, currentPoint.value)
      : null
  )

  const movedBearing = computed(() =>
    currentPoint.value && originalPoint.value && (movedDistanceM.value ?? 0) > 0
      ? bearingLabel(originalPoint.value, currentPoint.value)
      : null
  )

  const radiusPreset = computed(() => radiusPresetFor(unitType.value))

  const radiusChanged = computed(() => reachRadius.value !== originalRadius.value)

  /**
   * Justificativa só quando o usuário escolheu, nesta sessão, um raio diferente
   * do preset do tipo — um raio antigo fora do preset não vira trabalho extra
   * para quem só está mexendo no pino.
   */
  const isRadiusOffPreset = computed(
    () =>
      radiusChanged.value && reachRadius.value != null && reachRadius.value !== radiusPreset.value
  )

  const needsReason = computed(
    () =>
      !!originalPoint.value &&
      ((movedDistanceM.value ?? 0) > PIN_MOVE_THRESHOLD_M || radiusChanged.value)
  )

  const isReasonComplete = computed(
    () => !!reason.value && (reason.value !== 'OTHER' || !!reasonDetail.value.trim())
  )

  const canSave = computed(
    () =>
      canConfirm.value &&
      confirmedOnBuilding.value &&
      !!unitType.value &&
      (!needsReason.value || isReasonComplete.value) &&
      (!isRadiusOffPreset.value || !!radiusJustification.value.trim())
  )

  /** Troca o tipo e puxa o preset junto, a menos que o raio já tenha sido editado à mão. */
  function setUnitType(type: HealthUnitType): void {
    const wasPreset =
      reachRadius.value === undefined || reachRadius.value === radiusPresetFor(unitType.value)
    unitType.value = type
    if (wasPreset) reachRadius.value = radiusPresetFor(type)
  }

  /** Geocodifica o endereço textual (fluxo "Usar o endereço do cadastro"). */
  async function geocodeFromAddress(address: string): Promise<void> {
    try {
      const result = await Network.geocoding.geocodeAddress(address)
      formattedAddress.value = result.formattedAddress
      latitude.value = String(result.lat)
      longitude.value = String(result.lng)
      confidence.value = result.confidence
      origin.value = 'ADDRESS'
      foundLocation.value = true
    } catch (error) {
      notifyError(error, GEOCODE_FALLBACK)
    }
  }

  /** Alias explícito do passo 1: o ponto veio do endereço já cadastrado na unidade. */
  async function useRegisteredAddress(address: string): Promise<void> {
    await geocodeFromAddress(address)
  }

  /** Candidato escolhido na busca do Google Places (ponto do prédio, não do endereço). */
  function setFromPlace(candidate: PlaceCandidate): void {
    latitude.value = String(candidate.lat)
    longitude.value = String(candidate.lng)
    formattedAddress.value = candidate.address
    placeName.value = candidate.name
    confidence.value = candidate.confidence
    origin.value = 'PLACES'
    foundLocation.value = true
  }

  /** Extrai coordenadas do link colado (resolve short-url quando preciso). */
  async function applyMapsLink(): Promise<void> {
    if (!googleMapsLink.value) return

    isLoadingLink.value = true
    try {
      let url = googleMapsLink.value.trim()

      if (isShortGoogleMapsUrl(url)) {
        url = await Network.geocoding.resolveShortUrl(url)
      }

      const coords = parseGoogleMapsUrl(url)
      if (coords) {
        latitude.value = String(coords.lat)
        longitude.value = String(coords.lng)
        // O link entrega uma coordenada solta: nada garante que ela caia no prédio.
        origin.value = 'LINK'
        confidence.value = 'APPROXIMATE'
        foundLocation.value = true
        const address = await Network.geocoding.reverseGeocode(coords.lat, coords.lng)
        if (address) {
          formattedAddress.value = address
        }
      } else {
        // Validação local do link colado — não é falha de serviço.
        notifyErrorMessage('Não foi possível extrair coordenadas do link')
      }
    } catch (e) {
      notifyError(e, MAPS_LINK_FALLBACK)
    } finally {
      isLoadingLink.value = false
    }
  }

  function updateCoordinates(newCoordinates: { lat: number; lng: number }): void {
    latitude.value = String(newCoordinates.lat)
    longitude.value = String(newCoordinates.lng)
    // Arrastar a partir de Places/sugestão é ajuste fino sobre aquele ponto — a
    // origem continua sendo de onde ele veio. Vindo de endereço/link/nada, o
    // ponto passa a ser escolha manual de quem editou.
    if (origin.value !== 'SUGGESTION' && origin.value !== 'PLACES') {
      origin.value = 'MANUAL'
    }
    foundLocation.value = true
    // Mover o marcador invalida a confirmação feita sobre a posição anterior.
    confirmedOnBuilding.value = false
  }

  function resetLocation(): void {
    if (originalLatitude.value && originalLongitude.value) {
      latitude.value = originalLatitude.value
      longitude.value = originalLongitude.value
      confirmedOnBuilding.value = false
    }
  }

  /** Volta o marcador ao ponto sugerido pelo cluster de checks. */
  function centerOnSuggestion(): void {
    if (!suggestedPoint.value) return
    latitude.value = String(suggestedPoint.value.lat)
    longitude.value = String(suggestedPoint.value.lng)
    confirmedOnBuilding.value = false
  }

  /** Coordenadas, raio e tipo da unidade em edição. */
  function seedFromUnit(unit: SimpleHealthUnitResponse): void {
    latitude.value = unit.mainAddress?.latitude ?? null
    longitude.value = unit.mainAddress?.longitude ?? null
    originalLatitude.value = unit.mainAddress?.latitude ?? null
    originalLongitude.value = unit.mainAddress?.longitude ?? null
    // Unidade sem raio cadastrado (null, ou 0 vindo do adapter de detalhe) cai na
    // sugestão inicial, como no cadastro. O `originalRadius` recebe o mesmo valor
    // aplicado: sem isso, uma edição que não toca no raio já nasceria "alterada" e
    // pediria motivo à toa.
    const appliedRadius = unit.reachRadius ? Number(unit.reachRadius) : REACH_RADIUS_DEFAULT
    reachRadius.value = appliedRadius
    originalRadius.value = appliedRadius
    unitType.value = unit.type ?? null
    foundLocation.value = !!latitude.value && !!longitude.value
  }

  /** Abre o modal já no ponto sugerido, guardando o pino atual como original. */
  function seedFromSuggestion(suggestion: PinSuggestionItem): void {
    originalLatitude.value =
      suggestion.current.latitude != null ? String(suggestion.current.latitude) : null
    originalLongitude.value =
      suggestion.current.longitude != null ? String(suggestion.current.longitude) : null
    latitude.value = String(suggestion.suggested.latitude)
    longitude.value = String(suggestion.suggested.longitude)
    suggestedPoint.value = {
      lat: suggestion.suggested.latitude,
      lng: suggestion.suggested.longitude
    }
    unitType.value = suggestion.healthUnit.type ?? null
    originalRadius.value = suggestion.current.radiusM ?? undefined
    reachRadius.value = suggestion.suggested.radiusM ?? radiusPresetFor(unitType.value)
    origin.value = 'SUGGESTION'
    reason.value = 'APPLIED_SUGGESTION'
    suggestionId.value = suggestion.id
    confidence.value = null
    foundLocation.value = true
  }

  /** Setters para bindings em componentes filhos (evita mutação de prop). */
  function setGoogleMapsLink(value: string): void {
    googleMapsLink.value = value
  }

  function setReachRadius(value: number | string | undefined): void {
    reachRadius.value = value === undefined || value === '' ? undefined : Number(value)
  }

  function confirmLocation(): void {
    locationConfirmed.value = true
  }

  /** Editar o endereço invalida a verificação anterior. */
  function invalidateConfirmation(): void {
    locationConfirmed.value = false
  }

  /**
   * Payload do "Salvar pino". Lança em vez de devolver um objeto pela metade:
   * chegar aqui sem `canSave` é bug de rodapé, não entrada do usuário.
   */
  function toConfirmPayload(): PinConfirmPayload {
    if (!canSave.value) {
      throw new Error('Pino incompleto: confirme a posição, o tipo e as justificativas.')
    }

    return {
      latitude: Number(latitude.value),
      longitude: Number(longitude.value),
      reachRadius: Number(reachRadius.value),
      type: unitType.value as HealthUnitType,
      origin: origin.value ?? 'MANUAL',
      reason: needsReason.value ? reason.value : null,
      reasonDetail: reasonDetail.value,
      radiusJustification: isRadiusOffPreset.value ? radiusJustification.value : '',
      confirmedOnBuilding: true,
      suggestionId: suggestionId.value
    }
  }

  function reset(): void {
    latitude.value = null
    longitude.value = null
    originalLatitude.value = null
    originalLongitude.value = null
    formattedAddress.value = ''
    foundLocation.value = false
    locationConfirmed.value = false
    googleMapsLink.value = ''
    reachRadius.value = REACH_RADIUS_DEFAULT
    originalRadius.value = undefined
    unitType.value = null
    confidence.value = null
    origin.value = null
    placeName.value = ''
    reason.value = null
    reasonDetail.value = ''
    radiusJustification.value = ''
    confirmedOnBuilding.value = false
    suggestionId.value = null
    suggestedPoint.value = null
  }

  return {
    latitude,
    longitude,
    originalLatitude,
    originalLongitude,
    formattedAddress,
    foundLocation,
    locationConfirmed,
    googleMapsLink,
    isLoadingLink,
    reachRadius,
    unitType,
    confidence,
    origin,
    placeName,
    reason,
    reasonDetail,
    radiusJustification,
    confirmedOnBuilding,
    suggestionId,
    suggestedPoint,
    originalRadius,
    canConfirm,
    canResetLocation,
    currentPoint,
    originalPoint,
    movedDistanceM,
    movedBearing,
    radiusPreset,
    radiusChanged,
    isRadiusOffPreset,
    needsReason,
    canSave,
    setUnitType,
    geocodeFromAddress,
    useRegisteredAddress,
    setFromPlace,
    applyMapsLink,
    updateCoordinates,
    resetLocation,
    centerOnSuggestion,
    seedFromUnit,
    seedFromSuggestion,
    setGoogleMapsLink,
    setReachRadius,
    confirmLocation,
    invalidateConfirmation,
    toConfirmPayload,
    reset
  }
}

export type UnitGeolocationApi = ReturnType<typeof useUnitGeolocation>
